import React from "react";
import {
  Compass,
  Sparkles,
  Zap,
  Heart,
  MessageCircle,
  ShieldCheck,
  Target,
  TrendingUp,
} from "lucide-react";
import CourseBrochure from "./CourseBrochure";
import SEO from "./SEO";

const FRONTLINE = {
  slug: "frontline-elite",
  name: "Frontline Elite",
  eyebrow: "Frontline Elite · A 2-Day Retail Sales Sprint",
  coverTitle: {
    pre: "Turn your",
    accent: "retail job",
    post: "into a career.",
  },
  coverPromise:
    "A foundational sales training for ambitious salespeople in their first five years across India's premium and high-end retail. Two immersive days, 10 AM to 6 PM. Sixty-plus real scripts. One printed playbook. Built by 18 years of ground-up field expertise.",
  advisor: {
    role: "Programme Advisor · Nagpur",
    name: "Sweta Bhushan",
    phone: "+91 95036 73348",
    email: "sweta.bhushan@sebyx.com",
  },

  stats: [
    { k: "2 Days", v: "10 AM – 6 PM" },
    { k: "8 Modules", v: "32 lessons" },
    { k: "60% Drills", v: "Role-play, not lectures" },
    { k: "Certified", v: "Sebyx Frontline Elite" },
  ],
  whoItsFor: [
    "Ambitious salespeople in the first five years of their retail career.",
    "Sales professionals across premium and high-end retail — jewellery, fashion, auto, electronics, luxury — and customer-facing roles in retail banking, insurance & financial services.",
    "Ambitious people who want a career in sales, not just a paycheck.",
    "Store owners and retail chains training front-of-floor staff to actually close.",
  ],
  whatYouGet: [
    "Sebyx Frontline Elite Certification (printed + LinkedIn)",
    "40-page printed Playbook (yours to keep)",
    "60+ real scripts for the sales floor",
    "Alumni WhatsApp group access",
    "30-60-90 day self-scorecard",
    "Priority seat for the Master Hunter cohort",
  ],
  outcomes: [
    {
      title: "Confident first 30 seconds",
      detail: "Own the greeting, read the walk-in, set the tone. No more freezing at the door.",
    },
    {
      title: "Consultative discovery",
      detail: "Move from order-taker to trusted advisor. Ask 4 questions, unlock the real need.",
    },
    {
      title: "Objection-proof closes",
      detail: "Handle \"too expensive\", \"just looking\", and \"let me think\" without pushing or discounting.",
    },
    {
      title: "A repeat-customer book",
      detail: "Turn every walk-in into a WhatsApp contact, a follow-up, and a referral.",
    },
  ],
  agenda: [
    [
      { time: "10:00 · 11:30", title: "M1 — The Salesperson Mindset" },
      { time: "11:30 · 13:00", title: "M2 — Professional Presence & Brand" },
      { time: "14:00 · 15:30", title: "M3 — The First 30 Seconds (live drills)" },
      { time: "15:30 · 17:00", title: "M4 — Premium Customer Psychology" },
      { time: "17:00 · 18:00", title: "Debrief · scorecards · homework" },
    ],
    [
      { time: "10:00 · 11:30", title: "M5 — Consultative Selling" },
      { time: "11:30 · 13:00", title: "M6 — Objection Handling (live drills)" },
      { time: "14:00 · 15:30", title: "M7 — The Close & Ethical Upsell" },
      { time: "15:30 · 17:00", title: "M8 — Book of Repeat Customers" },
      { time: "17:00 · 18:00", title: "Final drill · certification · Q&A" },
    ],
  ],
  modules: [
    {
      n: "M1",
      hours: "90 min",
      Icon: Compass,
      title: "The Salesperson Mindset",
      lines: [
        "Career vs. job — the 5-year math",
        "Order-taker vs. salesperson",
        "The 4 identities of a great seller",
      ],
    },
    {
      n: "M2",
      hours: "90 min",
      Icon: Sparkles,
      title: "Professional Presence & Brand",
      lines: [
        "First 5-second signals",
        "Dressing on-code for premium",
        "Voice, posture, energy",
      ],
    },
    {
      n: "M3",
      hours: "90 min",
      Icon: Zap,
      title: "The First 30 Seconds",
      lines: [
        "3-step greeting choreography",
        "8 walk-in archetypes",
        "Killer phrases to avoid",
      ],
    },
    {
      n: "M4",
      hours: "90 min",
      Icon: Heart,
      title: "Premium Customer Psychology",
      lines: [
        "6 reasons people buy premium",
        "The luxury decision tree",
        "Discretion-sensitive selling",
      ],
    },
    {
      n: "M5",
      hours: "90 min",
      Icon: MessageCircle,
      title: "Consultative Selling",
      lines: [
        "4-question discovery",
        "Outcome selling, not features",
        "The three-option rule",
      ],
    },
    {
      n: "M6",
      hours: "90 min",
      Icon: ShieldCheck,
      title: "Objection Handling",
      lines: [
        "\"Too expensive\"",
        "\"Just looking\" · \"Let me think\"",
        "\"Let me ask my partner\"",
      ],
    },
    {
      n: "M7",
      hours: "90 min",
      Icon: Target,
      title: "The Close & Ethical Upsell",
      lines: [
        "4 clean closing techniques",
        "Upsell without pressure",
        "Second-visit playbook",
      ],
    },
    {
      n: "M8",
      hours: "90 min",
      Icon: TrendingUp,
      title: "Book of Repeat Customers",
      lines: [
        "WhatsApp follow-up etiquette",
        "Your Little Black Book",
        "Asking for referrals",
      ],
    },
  ],
};

export default function FrontlineBrochure() {
  return (
    <>
      <SEO
        title="Frontline Elite Masterclass | Premium Retail Sales Training"
        description="Turn showroom walk-ins into lifetime clients. Learn behavioral sales frameworks and 60+ exact verbal scripts to disarm price objections. Double your floor commissions."
        path="/frontline-brochure"
      />
      <CourseBrochure course={FRONTLINE} />
    </>
  );
}
